// Account (spec/15 P-20): who you are and how others see you, your email, password and two-factor, the accounts you
// sign in with, where you are signed in, what we send you, the terms you agreed to, and how the site looks. All of it
// the caller's own (`/v1/me`, better-auth's own endpoints), so nothing here needs a permission.

import type { SignedInSession } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Badge, Button, Skeleton, Switch, TextField, useToast } from '@clavian/ui-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { queryKeys, useBrandLegal, useConsents, useLinkedAccounts, useMe, useMySessions, useSession } from '../api/queries';
import { ThemeSwitch, useSignOut } from '../shell/AppShell';
import { NavIcon } from '../shell/nav-icons';
import { Page } from '../shell/Page';
import { ConnectedAccountsSection } from './ConnectedAccounts';
import { TwoFactorSection } from './TwoFactor';

export function AccountPage(): ReactNode {
  const { t } = useTranslation('player');
  const signOut = useSignOut();
  const problem = useProblem();
  const { show } = useToast();

  return (
    <Page title={t('account.title')} description={t('account.subtitle')}>
      <div className="pw-account">
        <ProfileSection />
        <EmailSection />
        <PasswordSection />
        <TwoFactorSection />
        <ConnectedAccountsSection />
        <DevicesSection />
        <NotificationsSection />
        <LegalSection />
        <section className="pw-panel" aria-labelledby="pw-appearance">
          <h2 className="pw-section-title" id="pw-appearance">
            {t('account.appearance.title')}
          </h2>
          <ThemeSwitch />
        </section>
        <Button
          variant="secondary"
          size="lg"
          block
          icon={<NavIcon name="signout" size={20} />}
          onClick={() => void signOut().catch((error: unknown) => show({ title: problem.text(error, { fallback: t('account.signOutError') }), tone: 'danger' }))}
        >
          {t('account.signOut')}
        </Button>
      </div>
    </Page>
  );
}

/** The display name, editable, beside the gamer tag. */
function ProfileSection(): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const queryClient = useQueryClient();
  const problem = useProblem();
  const { show } = useToast();
  const me = useMe();
  const user = useSession().data?.user;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  const save = useMutation({
    mutationFn: (displayName: string) => api.completeMyProfile({ body: { displayName } }),
    onSuccess: async (profile) => {
      queryClient.setQueryData(queryKeys.me, profile);
      await queryClient.invalidateQueries({ queryKey: queryKeys.session });
      setEditing(false);
      show({ title: t('account.profile.saved'), tone: 'ok' });
    },
    onError: (error) => {
      // A name the server refused is said beside the field; any other refusal is a toast with its own words.
      const named = problem.fieldMessage(error, 'displayName');
      setNameError(named);
      if (named === undefined) show({ title: problem.text(error, { fallback: t('account.profile.saveError') }), tone: 'danger' });
    },
  });

  if (me.data === undefined) {
    return (
      <section className="pw-panel">
        <Skeleton shape="text" lines={3} />
      </section>
    );
  }
  const displayName = me.data.displayName?.trim() || user?.name?.trim() || me.data.username?.trim() || t('account.profile.notSet');

  return (
    <section className="pw-panel" aria-labelledby="pw-profile">
      <h2 className="pw-section-title" id="pw-profile">
        {t('account.profile.title')}
      </h2>
      <div className="pw-profile">
        <Avatar name={displayName} size="xl" decorative />
        <div className="pw-profile__who">
          <span className="pw-profile__name">{displayName}</span>
          {me.data.username ? <span className="pw-subtle">@{me.data.username}</span> : null}
        </div>
        {editing ? null : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setName(me.data.displayName ?? '');
              setEditing(true);
            }}
          >
            {t('account.profile.edit')}
          </Button>
        )}
      </div>
      {editing ? (
        <form
          className="pw-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() !== '') save.mutate(name.trim());
          }}
        >
          <TextField label={t('account.profile.name')} description={t('account.profile.nameHint')} value={name} maxLength={50} autoComplete="nickname" error={nameError} onChange={(event) => setName(event.target.value)} />
          <div className="pw-form__actions">
            <Button type="submit" variant="primary" busy={save.isPending} disabled={name.trim() === ''}>
              {t('account.profile.save')}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              {t('account.profile.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

/** The email, and changing it: a code to the new address, then the change (spec/07 §3.3). */
function EmailSection(): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const queryClient = useQueryClient();
  const problem = useProblem();
  const { show } = useToast();
  const me = useMe();
  const [step, setStep] = useState<'view' | 'enter' | 'code'>('view');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [destination, setDestination] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  // A stale sign-in and a malformed address have their own lines here; every other refusal says what its code says.
  const failureOf = (error: unknown): string => {
    switch (problemCode(error)) {
      case PROBLEM.reauthenticationRequired:
        return t('account.email.fresh');
      case PROBLEM.validationFailed:
        return problem.fieldMessage(error, 'email') ?? t('account.email.invalid');
      default:
        return problem.text(error, { fallback: t('account.email.failed') });
    }
  };

  const start = useMutation({
    mutationFn: (next: string) => api.updateMe({ body: { email: next } }),
    onSuccess: (answer) => {
      setFailure(null);
      if ('destination' in answer) {
        setDestination(answer.destination);
        setStep('code');
      }
    },
    onError: (error) => setFailure(failureOf(error)),
  });
  const confirm = useMutation({
    mutationFn: (otp: string) => api.confirmContactChange({ body: { code: otp } }),
    onSuccess: async (profile) => {
      queryClient.setQueryData(queryKeys.me, profile);
      await queryClient.invalidateQueries({ queryKey: queryKeys.session });
      setStep('view');
      setCode('');
      setEmail('');
      show({ title: t('account.email.changed'), tone: 'ok' });
    },
    onError: (error) => setFailure(failureOf(error)),
  });

  const current = me.data?.email ?? null;
  return (
    <section className="pw-panel" aria-labelledby="pw-email">
      <h2 className="pw-section-title" id="pw-email">
        {t('account.email.title')}
      </h2>
      {me.data === undefined ? (
        <Skeleton shape="text" lines={1} />
      ) : (
        <div className="pw-setting">
          <div className="pw-setting__text">
            <span className="pw-setting__value">{current ?? t('account.email.none')}</span>
            {current === null ? null : (
              <Badge tone={me.data.emailVerified ? 'ok' : 'warn'}>{me.data.emailVerified ? t('account.email.verified') : t('account.email.unverified')}</Badge>
            )}
          </div>
          {step === 'view' ? (
            <Button variant="secondary" size="sm" onClick={() => setStep('enter')}>
              {current === null ? t('account.email.add') : t('account.email.change')}
            </Button>
          ) : null}
        </div>
      )}
      {failure === null ? null : (
        <div className="pw-alert" role="alert">
          <p>{failure}</p>
          {problemCode(start.error) === PROBLEM.reauthenticationRequired ? (
            <p>
              <Link to="/sign-in" search={{ redirect: '/account' }}>
                {t('account.signInAgain')}
              </Link>
            </p>
          ) : null}
        </div>
      )}
      {step === 'enter' ? (
        <form
          className="pw-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            start.mutate(email.trim());
          }}
        >
          <TextField label={t('account.email.newEmail')} type="email" inputMode="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          <div className="pw-form__actions">
            <Button type="submit" variant="primary" busy={start.isPending} disabled={email.trim() === ''}>
              {t('account.email.send')}
            </Button>
            <Button variant="ghost" onClick={() => setStep('view')}>
              {t('account.email.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
      {step === 'code' ? (
        <form
          className="pw-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            confirm.mutate(code.trim());
          }}
        >
          <p role="status">{t('account.email.sentTo', { destination })}</p>
          <TextField label={t('account.email.code')} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value)} />
          <div className="pw-form__actions">
            <Button type="submit" variant="primary" busy={confirm.isPending} disabled={code.trim().length < 6}>
              {t('account.email.confirm')}
            </Button>
            <Button variant="ghost" onClick={() => setStep('view')}>
              {t('account.email.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

/** A password is set or changed with a code to the account's email (the reset flow, signed in). */
function PasswordSection(): ReactNode {
  const { t } = useTranslation('player');
  const me = useMe();
  const accounts = useLinkedAccounts();
  const hasPassword = (accounts.data ?? []).some((account) => account.providerId === 'credential');
  const email = me.data?.email ?? null;
  return (
    <section className="pw-panel" aria-labelledby="pw-password">
      <h2 className="pw-section-title" id="pw-password">
        {t('account.password.title')}
      </h2>
      <div className="pw-setting">
        <p className="pw-setting__text pw-muted">{email === null ? t('account.password.noEmail') : t('account.password.body', { email })}</p>
        {email === null ? null : (
          <Link to="/forgot-password" search={{ email, back: '/account' }} className="pw-btn pw-btn--secondary pw-btn--sm">
            {hasPassword ? t('account.password.change') : t('account.password.set')}
          </Link>
        )}
      </div>
    </section>
  );
}

function DevicesSection(): ReactNode {
  const { t } = useTranslation('player');
  const sessions = useMySessions();
  const all = sessions.data ?? [];
  const current = all.find((one) => one.current);
  const others = all.filter((one) => !one.current);
  return (
    <section className="pw-panel" aria-labelledby="pw-devices">
      <h2 className="pw-section-title" id="pw-devices">
        {t('account.devices.title')}
      </h2>
      {sessions.isPending ? (
        <Skeleton shape="text" lines={2} />
      ) : (
        <ul className="pw-rows" aria-labelledby="pw-devices">
          {current ? <DeviceRow session={current} /> : null}
          {others.map((one) => (
            <DeviceRow key={one.id} session={one} />
          ))}
          {others.length === 0 ? <li className="pw-subtle">{t('account.devices.empty')}</li> : null}
        </ul>
      )}
    </section>
  );
}

function DeviceRow({ session }: { readonly session: SignedInSession }): ReactNode {
  const { t } = useTranslation('player');
  const fmt = useVenueFormatter();
  const api = useApi();
  const queryClient = useQueryClient();
  const problem = useProblem();
  const { show } = useToast();
  const revoke = useMutation({
    mutationFn: () => api.revokeMySession({ path: { sessionId: session.id } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.mySessions }),
    onError: (error) => show({ title: problem.text(error), tone: 'danger' }),
  });
  const name = session.device?.trim() || t('account.devices.unknown');
  return (
    <li className="pw-row">
      <span className="pw-row__icon" aria-hidden="true">
        <NavIcon name="monitor" size={20} />
      </span>
      <span className="pw-row__main">
        <span className="pw-row__title">
          {name}
          {session.current ? (
            <>
              {' '}
              <Badge tone="session">{t('account.devices.current')}</Badge>
            </>
          ) : null}
        </span>
        <span className="pw-row__meta">{t('account.devices.lastActive', { time: fmt.relative(session.lastActiveAt) })}</span>
      </span>
      {session.current ? null : (
        <Button variant="ghost" size="sm" busy={revoke.isPending} aria-label={t('account.devices.signOutLabel', { device: name })} onClick={() => revoke.mutate()}>
          {t('account.devices.signOut')}
        </Button>
      )}
    </li>
  );
}

/** What reaches the player and how: marketing is theirs to switch; security and receipts are explained. */
function NotificationsSection(): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const queryClient = useQueryClient();
  const problem = useProblem();
  const { show } = useToast();
  const prefs = useQuery({ queryKey: queryKeys.notificationPrefs, queryFn: () => api.getNotificationPrefs(), retry: false });
  const save = useMutation({
    mutationFn: (optIn: boolean) => api.putNotificationPrefs({ body: { marketing: { optIn } } }),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.notificationPrefs, saved);
      show({ title: t('account.notifications.saved'), tone: 'ok' });
    },
    onError: (error) => show({ title: problem.text(error, { fallback: t('account.notifications.saveError') }), tone: 'danger' }),
  });

  return (
    <section className="pw-panel" aria-labelledby="pw-notifications">
      <h2 className="pw-section-title" id="pw-notifications">
        {t('account.notifications.title')}
      </h2>
      <p className="pw-muted">{t('account.notifications.intro')}</p>
      <ul className="pw-prefs">
        <li className="pw-pref">
          <div className="pw-pref__text">
            <span className="pw-pref__name">{t('account.notifications.security')}</span>
            <span className="pw-subtle">{t('account.notifications.securityHint')}</span>
          </div>
          <Badge tone="neutral">{t('account.notifications.alwaysOn')}</Badge>
        </li>
        <li className="pw-pref">
          <div className="pw-pref__text">
            <span className="pw-pref__name">{t('account.notifications.receipts')}</span>
            <span className="pw-subtle">{t('account.notifications.receiptsHint')}</span>
          </div>
          <Badge tone="neutral">{t('account.notifications.alwaysOn')}</Badge>
        </li>
        <li className="pw-pref">
          {prefs.data === undefined ? (
            <Skeleton shape="text" lines={1} />
          ) : (
            <div className="pw-pref__switch">
              <Switch
                label={t('account.notifications.marketing')}
                stateLabel
                checked={prefs.data.marketing.optIn}
                disabled={save.isPending}
                onCheckedChange={(next) => save.mutate(next)}
              />
              <span className="pw-subtle">{t('account.notifications.marketingHint')}</span>
            </div>
          )}
        </li>
      </ul>
    </section>
  );
}

function LegalSection(): ReactNode {
  const { t } = useTranslation('player');
  const { legal } = useBrandLegal();
  const consents = useConsents();
  if (legal === undefined) return null;
  const current = consents.data?.terms?.version === legal.terms.version && consents.data.privacy?.version === legal.privacy.version;
  return (
    <section className="pw-panel" aria-labelledby="pw-legal">
      <h2 className="pw-section-title" id="pw-legal">
        {t('account.legal.title')}
      </h2>
      {current ? <p className="pw-muted">{t('account.legal.accepted')}</p> : null}
      <p className="pw-legal-links">
        <a className="pw-link" href={legal.terms.url} target="_blank" rel="noreferrer">
          {t('account.legal.read', { document: t('account.legal.terms') })}
        </a>
        <a className="pw-link" href={legal.privacy.url} target="_blank" rel="noreferrer">
          {t('account.legal.read', { document: t('account.legal.privacy') })}
        </a>
      </p>
    </section>
  );
}

